import React, { useMemo, useState } from 'react'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { PanelButton } from '@components/Panel/PanelButton'
import { getPrivyLoginMethodFromLocalStorage, usePrivy } from '@towns/privy'
import { isSignUpWallet } from '@towns/privy/useGetSignUpWalletSigner'
import { Box, BoxProps, Button, Icon, Paragraph, Stack, Text } from '@ui'
import {
    isAbstractAccountAddress,
    useMyAbstractAccountAddress,
} from 'hooks/useAbstractAccountAddress'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import {
    Address,
    BlockchainTransactionType,
    TSigner,
    useConnectivity,
    useIsTransactionPending,
    useLinkEOAToRootKeyTransaction,
    useMyLinkedWallets,
    useMyUserId,
    usePrimaryWallet,
    useUnlinkWalletTransaction,
} from 'use-towns-client'

import { ConnectWalletThenLinkButton } from './ConnectWallet/ConnectWalletThenLinkButton'
import { ConnectWalletThenUnlinkViaCaller } from './ConnectWallet/ConnectWalletThenUnlinkViaCaller'
import { UnlinkPopup } from './Wallet/UnlinkPopup'
import { WalletWithBalance } from './Wallet/WalletWithBalance'
import { WalletLinkingInfo } from './WalletLinkingInfo'

export const WalletLinkingPanel = React.memo(() => {
    const userId = useMyUserId()

    const [unlinkModal, setUnlinkModal] = useState<{
        visible: boolean
        addressToUnlink?: Address
    }>({
        visible: false,
        addressToUnlink: undefined,
    })
    const showUnlinkModal = (addressToUnlink: Address) =>
        setUnlinkModal({
            visible: true,
            addressToUnlink: addressToUnlink,
        })
    const hideUnlinkModal = () =>
        setUnlinkModal({
            visible: false,
            addressToUnlink: undefined,
        })
    const { loggedInWalletAddress } = useConnectivity()

    const { linkEOAToRootKeyTransaction, isLoading: isSanctionsListLoading } =
        useLinkEOAToRootKeyTransaction({
            onError: (error) => {
                if (error?.message?.includes('Sanctions List')) {
                    popupToast(({ toast }) => (
                        <StandardToast.Error
                            message="Cannot link this wallet"
                            subMessage="This wallet has been blocked by OFAC's Sanctions List"
                            toast={toast}
                        />
                    ))
                }
                return Promise.resolve()
            },
        })
    const { unlinkWalletTransaction } = useUnlinkWalletTransaction()

    const { data: aaAddress } = useMyAbstractAccountAddress()

    const { data: _linkedWallets } = useMyLinkedWallets()
    const linkedWallets = useMemo(
        () =>
            _linkedWallets
                ?.slice()
                .sort((a) => (a.toLowerCase() === aaAddress?.toLowerCase() ? -1 : 1)),
        [_linkedWallets, aaAddress],
    )

    const handleLinkWallet = async (
        rootKey: TSigner | undefined,
        wallet: TSigner | undefined,
    ): Promise<void> => {
        if (!wallet) {
            return
        }
        await linkEOAToRootKeyTransaction(rootKey, wallet)
    }

    async function onUnlinkClick(addressToUnlink: Address, getSigner: GetSigner) {
        const signer = await getSigner()
        if (!signer) {
            createPrivyNotAuthenticatedNotification()
            return
        }
        unlinkWalletTransaction(signer, addressToUnlink)
    }

    const isWalletLinkingPending = useIsTransactionPending(BlockchainTransactionType.LinkWallet)
    const isWalletUnLinkingPending = useIsTransactionPending(BlockchainTransactionType.UnlinkWallet)
    const { user } = usePrivy()
    const signUpWalletMetadata = user?.linkedAccounts?.find(isSignUpWallet)

    if (!loggedInWalletAddress) {
        return null
    }

    const isDisabled = isWalletLinkingPending || isWalletUnLinkingPending || isSanctionsListLoading
    const isEOASignin = getPrivyLoginMethodFromLocalStorage() === 'siwe'

    return (
        <Stack gap grow position="relative" overflow="auto">
            {linkedWallets?.map((a) => {
                return (
                    <LinkedWallet
                        address={a as Address}
                        aaAddress={aaAddress}
                        key={a}
                        userId={userId}
                        onUnlinkClick={showUnlinkModal}
                    />
                )
            })}

            <ConnectWalletThenLinkButton onLinkWallet={handleLinkWallet}>
                {({ onClick }) => (
                    <PanelButton
                        cursor={isDisabled ? 'not-allowed' : 'pointer'}
                        opacity={isDisabled ? '0.5' : 'opaque'}
                        disabled={isDisabled}
                        tooltip={isSanctionsListLoading ? 'Loading sanctions lists...' : undefined}
                        onClick={onClick}
                    >
                        <Box width="height_md" alignItems="center">
                            <Icon type="link" size="square_sm" />
                        </Box>
                        <Paragraph color="default">Link new wallet</Paragraph>
                    </PanelButton>
                )}
            </ConnectWalletThenLinkButton>

            <WalletLinkingInfo />
            <Box grow justifyContent="end">
                {isEOASignin &&
                    linkedWallets &&
                    linkedWallets.length > 0 &&
                    signUpWalletMetadata?.address &&
                    linkedWallets.every(
                        (a) => a.toLowerCase() !== signUpWalletMetadata?.address.toLowerCase(),
                    ) && (
                        <Stack padding="md" rounded="sm" background="negativeSubtle">
                            <Text size="sm">
                                You signed in with a wallet (
                                {shortAddress(signUpWalletMetadata?.address)}), but it has not been
                                linked to your Towns account. Before linking it, you may need to
                                unlink it from an existing account.
                            </Text>
                        </Stack>
                    )}

                <ConnectWalletThenUnlinkViaCaller isDisabled={isDisabled} />
            </Box>

            {unlinkModal.visible && (
                <ModalContainer asSheet minWidth="auto" onHide={hideUnlinkModal}>
                    <Box padding="sm" gap="lg" alignItems="center">
                        <Text>Are you sure you want to unlink</Text>
                        <Text
                            fontSize={{
                                mobile: 'sm',
                            }}
                        >
                            {unlinkModal.addressToUnlink}?
                        </Text>
                        <Box horizontal gap>
                            <Button tone="level2" onClick={hideUnlinkModal}>
                                Cancel
                            </Button>
                            <WalletReady>
                                {({ getSigner }) => (
                                    <Button
                                        tone="negative"
                                        onClick={() => {
                                            if (unlinkModal.addressToUnlink) {
                                                onUnlinkClick(
                                                    unlinkModal.addressToUnlink,
                                                    getSigner,
                                                )
                                                hideUnlinkModal()
                                            }
                                        }}
                                    >
                                        Confirm
                                    </Button>
                                )}
                            </WalletReady>
                        </Box>
                    </Box>
                </ModalContainer>
            )}
        </Stack>
    )
})

export function FullPanelOverlay({
    text,
    subText,
    background,
    withSpinner = true,
    opacity = '0.9',
}: {
    text?: string
    subText?: string
    background?: BoxProps['background']
    withSpinner?: boolean
    opacity?: BoxProps['opacity']
}) {
    return (
        <Stack absoluteFill centerContent zIndex="above">
            <Stack
                opacity={opacity}
                position="absolute"
                background={background}
                style={!background ? { background: 'var(--background)' } : undefined}
                width="100%"
                height="100%"
            />
            <Stack gap="lg" position="relative">
                <Stack centerContent gap="sm">
                    <Text>{text}</Text>
                    {subText && <Text>{subText}</Text>}
                </Stack>
                {withSpinner && <ButtonSpinner />}
            </Stack>
        </Stack>
    )
}

export function LinkedWallet({
    address,
    aaAddress,
    height = 'x8',
    userId,
}: {
    address: Address
    aaAddress: Address | undefined
    userId?: string
    onUnlinkClick?: (address: Address) => void
    height?: BoxProps['height']
}) {
    const { openPanel } = usePanelActions()
    const [isUnlinkPopupOpen, setIsUnlinkPopupOpen] = useState(false)

    const isAbstractAccount =
        aaAddress && isAbstractAccountAddress({ address, abstractAccountAddress: aaAddress })

    const onWalletClick = () => {
        if (isUnlinkPopupOpen) {
            return
        }
        if (isAbstractAccount) {
            openPanel('wallet', { assetSource: address })
        }
    }

    const { data: primaryWallet } = usePrimaryWallet({ rootWallet: userId })

    const isPrimaryWallet = useMemo(() => {
        if (isAbstractAccount) {
            return primaryWallet?.type === 'smartAccount'
        }
        return primaryWallet?.address?.toLowerCase() === address.toLowerCase()
    }, [primaryWallet, address, isAbstractAccount])

    // TODO: we have a privy wallet, and AA wallet. Probably we want to filter out the privy wallet, and only show AA wallet address. Do we need to have our own UI for AA wallet assets? Since you can't export it to MM
    return (
        <PanelButton
            as="div"
            background="level2"
            hoverable={isAbstractAccount}
            cursor={isAbstractAccount ? 'pointer' : 'auto'}
            justifyContent="spaceBetween"
            height={height}
            onClick={onWalletClick}
        >
            <WalletWithBalance
                address={address}
                isAbstractAccount={!!isAbstractAccount}
                isPrimaryWallet={isPrimaryWallet}
                CardPopup={
                    userId ? (
                        <UnlinkPopup
                            isAbstractAccount={!!isAbstractAccount}
                            setIsUnlinkPopupOpen={setIsUnlinkPopupOpen}
                            address={address}
                            isPrimaryWallet={isPrimaryWallet}
                            userId={userId}
                        />
                    ) : null
                }
            />
        </PanelButton>
    )
}
